import React, { useState, useEffect, useCallback } from 'react';
import { getHostelRequests, approveHostelRequest, rejectHostelRequest } from '../api';
import {
  FiAlertTriangle, FiCheck, FiClipboard, FiX, FiChevronDown, FiChevronUp,
  FiEdit2, FiTrash2, FiLogOut, FiCheckSquare, FiCoffee,
} from 'react-icons/fi';
import { usePermissions } from '../utils/permissions';

const STATUS_META = {
  pending: { color: '#c48a00', badge: 'badge-gold' },
  approved: { color: '#059669', badge: 'badge-success' },
  rejected: { color: '#dc2626', badge: 'badge-danger' },
};

const CATEGORY_META = {
  edit: { label: 'Resident Edit', icon: FiEdit2, color: '#3b6fd4' },
  delete: { label: 'Resident Delete', icon: FiTrash2, color: '#dc2626' },
  checkout: { label: 'Checkout', icon: FiLogOut, color: '#7c3aed' },
  attendance_edit: { label: 'Attendance Correction', icon: FiCheckSquare, color: '#c48a00' },
  meal_edit: { label: 'Meal Correction', icon: FiCoffee, color: '#059669' },
};

function formatJson(data) {
  try {
    const parsed = typeof data === 'string' ? JSON.parse(data) : data;
    return Object.entries(parsed).map(([k, v]) => `${k}: ${v}`).join('\n');
  } catch { return String(data); }
}

export default function HostelRequests() {
  const { can } = usePermissions();
  const canEdit = can('hostel_requests', 'edit');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [actionModal, setActionModal] = useState(null); // { request, type: 'approve'|'reject' }
  const [adminNotes, setAdminNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [expandedData, setExpandedData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await getHostelRequests(params);
      setRequests(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async () => {
    if (!actionModal) return;
    setProcessing(true);
    try {
      if (actionModal.type === 'approve') {
        await approveHostelRequest(actionModal.request.id, { admin_notes: adminNotes });
      } else {
        await rejectHostelRequest(actionModal.request.id, { admin_notes: adminNotes });
      }
      setActionModal(null);
      setAdminNotes('');
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Action failed');
    }
    setProcessing(false);
  };

  const visible = categoryFilter ? requests.filter(r => r.request_type === categoryFilter) : requests;
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const categoryCounts = requests.reduce((acc, r) => { acc[r.request_type] = (acc[r.request_type] || 0) + 1; return acc; }, {});

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title"><FiClipboard /> Hostel Requests</h2>
          <p style={{ color: 'var(--text-muted)', margin: '4px 0 0' }}>Review and action edit/delete/checkout and attendance/meal correction requests from hostels</p>
        </div>
      </div>

      {pendingCount > 0 && statusFilter !== 'pending' && (
        <div className="alert alert-warning" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiAlertTriangle /> {pendingCount} pending request(s) need your attention
        </div>
      )}

      {/* Status filter */}
      <div style={{ display: 'flex', gap: '8px', margin: '18px 0 12px', flexWrap: 'wrap' }}>
        {['pending', 'approved', 'rejected', ''].map(s => {
          const meta = STATUS_META[s] || { color: '#5b6785' };
          const active = statusFilter === s;
          return (
            <button
              key={s || 'all'}
              onClick={() => setStatusFilter(s)}
              style={{
                padding: '6px 16px', border: `1.5px solid ${meta.color}`, borderRadius: '999px', cursor: 'pointer',
                background: active ? meta.color : 'transparent', color: active ? '#fff' : meta.color,
                fontWeight: 700, fontSize: '12.5px', textTransform: 'uppercase', letterSpacing: '.02em',
              }}
            >
              {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All'}
              {s === 'pending' && pendingCount > 0 && (
                <span style={{ marginLeft: '6px', background: active ? 'rgba(255,255,255,0.3)' : '#dc2626', color: '#fff', borderRadius: '999px', padding: '1px 7px', fontSize: '11px' }}>
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Category filter */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setCategoryFilter('')}
          className={`btn btn-small ${!categoryFilter ? 'btn-dark' : 'btn-secondary'}`}
        >
          All Categories
        </button>
        {Object.entries(CATEGORY_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const count = categoryCounts[key] || 0;
          if (!count) return null;
          return (
            <button
              key={key}
              onClick={() => setCategoryFilter(categoryFilter === key ? '' : key)}
              className={`btn btn-small ${categoryFilter === key ? 'btn-dark' : 'btn-secondary'}`}
            >
              <Icon /> {meta.label} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {visible.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', background: '#f7f8fc', borderRadius: '10px' }}>
              No matching requests found.
            </div>
          ) : visible.map(req => {
            const meta = CATEGORY_META[req.request_type] || { label: req.request_type, icon: FiClipboard, color: '#5b6785' };
            const Icon = meta.icon;
            const sMeta = STATUS_META[req.status] || {};
            return (
              <div key={req.id} style={{ background: '#f7f8fc', border: `1px solid ${sMeta.color || '#e4e8f4'}`, borderRadius: '10px', padding: '16px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: `${meta.color}18`, color: meta.color, padding: '3px 11px', borderRadius: '999px', fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase' }}>
                        <Icon size={12} /> {meta.label}
                      </span>
                      <span className={`badge ${sMeta.badge || 'badge-muted'}`}>{req.status}</span>
                    </div>
                    <h4 style={{ color: 'var(--text-primary)', margin: '8px 0 4px', fontSize: '15px' }}>{req.resident_name || 'Unknown Resident'}</h4>
                    <p style={{ color: 'var(--text-muted)', margin: 0, fontSize: '13px' }}>
                      Hostel: <span style={{ color: 'var(--color-primary)', fontWeight: 600 }}>{req.hostel_name}</span> ·{' '}
                      Submitted: {req.created_at ? new Date(req.created_at).toLocaleDateString('en-PK') : '—'}
                      {req.resolved_at && ` · Resolved: ${new Date(req.resolved_at).toLocaleDateString('en-PK')}`}
                    </p>
                  </div>

                  {req.status === 'pending' && canEdit && (
                    <div className="action-buttons">
                      <button className="btn btn-primary btn-small" onClick={() => { setActionModal({ request: req, type: 'approve' }); setAdminNotes(''); }}>
                        <FiCheck /> Approve
                      </button>
                      <button className="btn btn-danger btn-small" onClick={() => { setActionModal({ request: req, type: 'reject' }); setAdminNotes(''); }}>
                        <FiX /> Reject
                      </button>
                    </div>
                  )}
                </div>

                {req.reason && (
                  <div style={{ marginTop: '10px', padding: '8px 12px', background: '#fff', borderRadius: '6px', fontSize: '13px', color: 'var(--text-primary)' }}>
                    <strong>Reason:</strong> {req.reason}
                  </div>
                )}

                {req.requested_data && (
                  <div style={{ marginTop: '8px' }}>
                    <button
                      onClick={() => setExpandedData(expandedData === req.id ? null : req.id)}
                      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '12px', padding: '4px 0', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      {expandedData === req.id ? <FiChevronUp size={13} /> : <FiChevronDown size={13} />} {expandedData === req.id ? 'Hide' : 'View'} Requested Changes
                    </button>
                    {expandedData === req.id && (
                      <pre style={{ background: '#fef8ec', padding: '10px', borderRadius: '6px', fontSize: '12px', color: 'var(--color-primary)', marginTop: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {formatJson(req.requested_data)}
                      </pre>
                    )}
                  </div>
                )}

                {req.admin_notes && (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fef8ec', borderRadius: '6px', fontSize: '13px', color: 'var(--text-primary)' }}>
                    <strong>Admin Notes:</strong> {req.admin_notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Approve/Reject Modal */}
      {actionModal && (
        <div className="modal-overlay" onClick={() => setActionModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {actionModal.type === 'approve' ? <><FiCheck /> Approve Request</> : <><FiX /> Reject Request</>}
              </h3>
              <button className="modal-close" onClick={() => setActionModal(null)}><FiX /></button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: '12px' }}>
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                {actionModal.type === 'approve'
                  ? `This will apply the ${CATEGORY_META[actionModal.request.request_type]?.label || actionModal.request.request_type} to "${actionModal.request.resident_name}".`
                  : `This will reject the request from "${actionModal.request.hostel_name}".`}
              </p>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Admin Notes (optional)</label>
                <textarea className="form-control" rows="3" value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Add a note for the hostel..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setActionModal(null)}>Cancel</button>
              <button
                className={`btn ${actionModal.type === 'approve' ? 'btn-primary' : 'btn-danger'}`}
                onClick={handleAction} disabled={processing}
              >
                {processing ? 'Processing...' : (actionModal.type === 'approve' ? 'Confirm Approve' : 'Confirm Reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
